import type { ReactNode } from 'react'
import type { SupplyConfidence } from '@/api/client'
import type { ReviewMarker } from '@/lib/review-events'
import type { SupplyMarker } from '@/lib/supply-events'
import { cn } from '@/lib/utils'
import {
  MAX_BLOCK_ROWS_PER_DAY,
  eventBlockMeta,
  eventBlockTitle,
  type EventBlockInput,
  type EventBlockMeta,
  type EventBlockSpec,
} from './event-block-meta'

/**
 * The labelled blocks themselves (CAP-913). What an event says and looks like is
 * `eventBlockMeta`'s (./event-block-meta); here are the button, the legend chip
 * and the day cell's arrangement of them.
 */

/**
 * The collapsed day makes no claim about its events: planned and recorded,
 * sure and unsure, routine and money-moving all share one chip, and any one
 * colour or shape would be false for some mix of them. Their words are in its
 * tooltip and the day list it opens.
 */
export const EVENT_COUNT_CLASSES = 'border-border bg-surface2 text-text'

const FOCUS = 'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint'

/** An event's icon, drawn the same wherever it appears. Planned events get a lighter stroke. */
export function EventBlockIcon({
  meta,
  className,
}: {
  meta: EventBlockMeta
  className?: string
}): ReactNode {
  const { Icon, outlined } = meta
  return (
    <Icon
      className={cn('h-3 w-3 shrink-0', className)}
      strokeWidth={outlined ? 2 : 2.5}
      aria-hidden="true"
    />
  )
}

/** A legend entry: the look of one kind of event, from the same mapping the blocks use. */
export function EventLegendChip({
  input,
  children,
}: {
  input: EventBlockInput
  children: ReactNode
}) {
  const meta = eventBlockMeta(input)
  return (
    <span
      data-testid="event-legend-chip"
      className={cn(
        'inline-flex items-center gap-1 rounded border px-1 py-px text-[11px] leading-4',
        meta.classes
      )}
    >
      <EventBlockIcon meta={meta} />
      {children}
    </span>
  )
}

/** The deposit looks: an event that happened, and one that is planned. */
export function DepositLegend() {
  return (
    <>
      <EventLegendChip input={{ layer: 'deposit', type: 'placed', planned: false }}>
        Deposit event
      </EventLegendChip>
      <EventLegendChip input={{ layer: 'deposit', type: 'expected_lapse', planned: true }}>
        Planned
      </EventLegendChip>
    </>
  )
}

/**
 * The supply looks, one chip each: what a runout and a delivery are, and a
 * runout the model is unsure of. Given `markers` (the property tab's month),
 * only the looks among them; without, every one (the portfolio calendar's
 * legend, which does not change as it scrolls).
 */
export function SupplyLegend({ markers }: { markers?: SupplyMarker[] }) {
  const has = (test: (marker: SupplyMarker) => boolean) => !markers || markers.some(test)
  const sure = has(
    marker =>
      marker.kind === 'runout' && (marker.confidence === 'medium' || marker.confidence === 'high')
  )
  const unsure = has(
    marker =>
      marker.kind === 'runout' && marker.confidence !== 'medium' && marker.confidence !== 'high'
  )
  const delivered = has(marker => marker.kind === 'delivery')
  const input = (kind: 'runout' | 'delivery', confidence: SupplyConfidence) =>
    ({ layer: 'supply', kind, category: 'supplies', confidence }) as const
  return (
    <>
      {sure && <EventLegendChip input={input('runout', 'high')}>Runs out</EventLegendChip>}
      {unsure && <EventLegendChip input={input('runout', 'low')}>Maybe runs out</EventLegendChip>}
      {delivered && <EventLegendChip input={input('delivery', 'high')}>Delivered</EventLegendChip>}
    </>
  )
}

/**
 * The review looks, one chip each: a window still open, one that has closed,
 * and a review that landed. Given `markers`, only the looks among them;
 * without, every one.
 */
export function ReviewLegend({ markers }: { markers?: ReviewMarker[] }) {
  const has = (test: (marker: ReviewMarker) => boolean) => !markers || markers.some(test)
  const open = has(marker => marker.kind === 'closes' && marker.planned)
  const closed = has(marker => marker.kind === 'closes' && !marker.planned)
  const received = has(marker => marker.kind !== 'closes')
  const input = (kind: 'closes' | 'guest', planned: boolean) =>
    ({ layer: 'review', kind, planned, owed: false }) as const
  return (
    <>
      {open && <EventLegendChip input={input('closes', true)}>Window closes</EventLegendChip>}
      {closed && <EventLegendChip input={input('closes', false)}>Window closed</EventLegendChip>}
      {received && <EventLegendChip input={input('guest', false)}>Review received</EventLegendChip>}
    </>
  )
}

/** One labelled block: a button that opens the event's sheet. */
export function EventBlock({ spec }: { spec: EventBlockSpec }) {
  const { meta } = spec
  const title = eventBlockTitle(spec)
  return (
    <button
      type="button"
      data-testid={`${spec.layer}-event-block`}
      data-event-block=""
      data-tone={meta.tone}
      data-planned={meta.outlined ? 'true' : 'false'}
      {...spec.data}
      aria-label={`${title} - ${spec.action}`}
      title={title}
      onClick={spec.onOpen}
      className={cn(
        'flex w-full min-w-0 items-start gap-1 rounded border px-1 py-px text-left text-[11px] leading-4',
        meta.classes,
        FOCUS
      )}
    >
      <EventBlockIcon meta={meta} className="mt-0.5" />
      <span className="min-w-0 break-words line-clamp-3">
        {meta.shortWords ? (
          <>
            {/* Under about 136px of cell the short form, over it the whole. Both are
                text, so neither is "clipped": the name and tooltip carry the whole. */}
            <span className="@min-[136px]:hidden">{meta.shortWords}</span>
            <span className="hidden @min-[136px]:inline">{meta.words}</span>
          </>
        ) : (
          meta.words
        )}
      </span>
    </button>
  )
}

interface DayEventBlocksProps {
  /** This day's events, already in display order. */
  blocks: EventBlockSpec[]
  /** Property-local `YYYY-MM-DD`, for the names of the buttons that open the day list. */
  day: string
  onOpenDay: () => void
}

/**
 * The events of one day cell, every layer's together.
 *
 * Up to `MAX_BLOCK_ROWS_PER_DAY` rows; a busier day shows one fewer block and
 * a "+N more" row that opens the day list. The row grows to fit, and the week
 * grows with its busiest day: the cells of a week share one height.
 *
 * In a cell too narrow for words it is one button - the layers' icons and the
 * number of events - that opens the same list.
 */
export function DayEventBlocks({ blocks, day, onOpenDay }: DayEventBlocksProps) {
  if (blocks.length === 0) return null
  const overflowing = blocks.length > MAX_BLOCK_ROWS_PER_DAY
  const shown = overflowing ? blocks.slice(0, MAX_BLOCK_ROWS_PER_DAY - 1) : blocks
  const hidden = blocks.length - shown.length
  const layers = [...new Set(blocks.map(block => block.layer))]
  const noun = blocks.length === 1 ? 'event' : 'events'

  return (
    // The wrapper is the container the two arrangements switch on.
    <div className="@container -mx-1" data-testid="day-events">
      {/* A cell under 80px (its content) cannot fit a word beside an icon without
          breaking it, so it shows icon and count instead. Keyed to the cell's own
          width and not the screen's, the way the cleaning bars are: the same week
          sits in a 160px cell on a wide monitor and a 40px one on a phone. */}
      <div className="hidden flex-col gap-0.5 @min-[80px]:flex">
        {shown.map(block => (
          <EventBlock key={block.key} spec={block} />
        ))}
        {overflowing && (
          <button
            type="button"
            data-testid="day-events-more"
            aria-label={`${hidden} more ${hidden === 1 ? 'event' : 'events'} on ${day} - open the day's list`}
            onClick={onOpenDay}
            className={cn(
              'w-full rounded border border-border px-1 py-px text-left text-[11px] leading-4 text-textMuted hover:text-text',
              FOCUS
            )}
          >
            +{hidden} more
          </button>
        )}
      </div>
      <button
        type="button"
        data-testid="day-events-count"
        aria-label={`${blocks.length} ${noun} on ${day} - open the day's list`}
        title={blocks.map(block => eventBlockTitle(block)).join('\n')}
        onClick={onOpenDay}
        className={cn(
          'flex w-full items-center justify-center gap-1 rounded border px-1 py-px text-[11px] font-medium leading-4 tabular-nums',
          EVENT_COUNT_CLASSES,
          '@min-[80px]:hidden',
          FOCUS
        )}
      >
        {layers.map((layer, index) => (
          <EventBlockIcon
            key={layer}
            meta={blocks.find(block => block.layer === layer)!.meta}
            // A second layer's icon needs a cell wide enough for two.
            className={index > 0 ? 'hidden @min-[48px]:block' : undefined}
          />
        ))}
        {blocks.length}
      </button>
    </div>
  )
}
